// navigation.js
import Headroom from 'headroom.js';

export default function SiteNavigation(options = {}) {
  const primaryNavigation = document.getElementById('primary-navigation');
  const navToggle = document.getElementById('nav-toggle');
  if (!navToggle || !primaryNavigation) return;

  // Prevent double init
  if (navToggle.dataset.navInit === 'true') return;
  navToggle.dataset.navInit = 'true';

  // Config
  const config = {
    inertSelectors: options.inertSelectors || ['main', '.standard-footer'],
  };

  // A11y wiring
  if (!navToggle.hasAttribute('aria-controls')) {
    navToggle.setAttribute('aria-controls', primaryNavigation.id);
  }
  navToggle.setAttribute('aria-expanded', 'false');
  primaryNavigation.setAttribute('aria-hidden', 'true');

  // Headroom (only once)
  if (!primaryNavigation.dataset.headroomInit) {
    const headroom = new Headroom(primaryNavigation);
    headroom.init();
    primaryNavigation.dataset.headroomInit = 'true';
  }

  // State
  const state = {
    isOpen: false,
    scrollY: 0,
    focusables: [],
    first: null,
    last: null,
    prevFocused: null,
    inertTargets: [],
  };

  // Handlers
  const onToggleClick = () => (state.isOpen ? close() : open());
  const onKeydown = (e) => {
    if (!state.isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab' && state.focusables.length) {
      // Simple focus trap
      if (e.shiftKey && document.activeElement === state.first) {
        e.preventDefault();
        state.last.focus();
      } else if (!e.shiftKey && document.activeElement === state.last) {
        e.preventDefault();
        state.first.focus();
      }
    }
  };

  navToggle.addEventListener('click', onToggleClick);
  document.addEventListener('keydown', onKeydown, true);

  function open() {
    if (state.isOpen) return;
    state.isOpen = true;
    state.prevFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    primaryNavigation.classList.add('primary-navigation--open');
    updateA11y(true);

    lockScroll();

    // Inert <main> and all .standard-footer elements
    state.inertTargets = getInertTargets();
    setInert(state.inertTargets, true);

    // Setup focusables and move focus inside
    captureFocusables();
    (state.first || primaryNavigation).focus();
  }

  function close() {
    if (!state.isOpen) return;
    state.isOpen = false;

    primaryNavigation.classList.remove('primary-navigation--open');
    updateA11y(false);

    setInert(state.inertTargets, false);
    unlockScroll();

    // Return focus to the toggle
    (state.prevFocused || navToggle).focus();
  }

  function updateA11y(isOpen) {
    navToggle.setAttribute('aria-expanded', String(isOpen));

    // Update SR-only text
    const sr = navToggle.querySelector('.sr-only');
    if (sr) {
      sr.textContent = isOpen ? 'Close Navigation Drawer' : 'Open Navigation Drawer';
    }

    // Hide nav from SR when closed
    primaryNavigation.setAttribute('aria-hidden', String(!isOpen));
  }

  function captureFocusables() {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    state.focusables = Array.from(primaryNavigation.querySelectorAll(selector))
      .filter(el => !el.hasAttribute('disabled') && isVisible(el));
    state.first = state.focusables[0] || null;
    state.last = state.focusables[state.focusables.length - 1] || null;
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function lockScroll() {
    state.scrollY = window.scrollY || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.height = '100dvh'; // helps on mobile browsers
    document.body.classList.add('no-scroll');
  }

  function unlockScroll() {
    document.body.classList.remove('no-scroll');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = '';
    window.scrollTo(0, state.scrollY || 0);
  }

  function getInertTargets() {
    const targets = [];
    config.inertSelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (!primaryNavigation.contains(el) && el !== navToggle) targets.push(el);
      });
    });
    return targets;
  }

  function setInert(els, val) {
    els.forEach((el) => {
      if (val) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true'); // SR fallback
      } else {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      }
    });
  }

  // Optionally expose programmatic control
  return { open, close };
}